/**
 * Chatwoot identifier hash callable
 *
 * Fetches HMAC `identifier_hash` for `setUser` after Firebase Auth; requires signed-in user.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../firebaseConfig';
import { CHATWOOT_WEB_APP_ONBOARDING_PRODUCT } from './chatwootProduct';

type OnboardingChatwootHashRequest = {
  product: typeof CHATWOOT_WEB_APP_ONBOARDING_PRODUCT;
};

export type GetChatwootIdentifierHashResponse = {
  identifier: string;
  identifierHash: string;
  algorithm?: string;
  issuedAt?: number;
};

export type CallGetChatwootIdentifierHashResult =
  | { ok: true; data: GetChatwootIdentifierHashResponse }
  | { ok: false; code: string; message: string };

const call = httpsCallable<OnboardingChatwootHashRequest, Record<string, unknown>>(
  functions,
  'getChatwootIdentifierHash',
);

function parseSuccess(data: Record<string, unknown>): GetChatwootIdentifierHashResponse | null {
  const identifierHash =
    (typeof data.identifierHash === 'string' && data.identifierHash) ||
    (typeof data.identifier_hash === 'string' && data.identifier_hash) ||
    null;
  if (!identifierHash?.trim()) return null;

  const identifier = typeof data.identifier === 'string' && data.identifier.trim() !== '' ? data.identifier : null;
  if (!identifier) return null;

  const algorithm = typeof data.algorithm === 'string' ? data.algorithm : undefined;
  const issuedAt = typeof data.issuedAt === 'number' ? data.issuedAt : undefined;
  return { identifier, identifierHash: identifierHash.trim(), algorithm, issuedAt };
}

function isFirebaseLikeError(e: unknown): e is { code: string; message: string } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'code' in e &&
    typeof (e as { code: unknown }).code === 'string' &&
    'message' in e &&
    typeof (e as { message: unknown }).message === 'string'
  );
}

/**
 * Fetches HMAC for Chatwoot `setUser` (`identifier_hash`). Caller must be authenticated —
 * the backend uses `request.auth.uid` as the identifier; use the response `identifier` for `setUser`.
 */
export async function callGetChatwootIdentifierHash(): Promise<CallGetChatwootIdentifierHashResult> {
  try {
    const res = await call({ product: CHATWOOT_WEB_APP_ONBOARDING_PRODUCT });
    const raw = res.data;
    if (!raw || typeof raw !== 'object') {
      return { ok: false, code: 'invalid-response', message: 'Empty or invalid response' };
    }
    const parsed = parseSuccess(raw as Record<string, unknown>);
    if (!parsed) {
      return { ok: false, code: 'invalid-response', message: 'Missing identifierHash or identifier' };
    }
    return { ok: true, data: parsed };
  } catch (e: unknown) {
    if (isFirebaseLikeError(e)) {
      return { ok: false, code: e.code, message: e.message };
    }
    const message = e instanceof Error ? e.message : String(e);
    return { ok: false, code: 'unknown', message };
  }
}
